<template>
  <div class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50">
    <div class="bg-gray-800 p-6 rounded-md shadow-lg w-full max-w-4xl mx-4">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-white">User Verification</h2>
        <button @click="$emit('closeModal')" class="text-gray-400 hover:text-white">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Main content - split into left and right -->
      <div class="flex flex-col lg:flex-row gap-6">
        <!-- Left side - User Information -->
        <div class="flex-1 bg-gray-700 p-5 rounded-md">
          <h3 class="text-lg font-medium text-white mb-4">User Information</h3>

          <div class="space-y-4">
            <div>
              <label class="block text-gray-400 text-sm">Full Name</label>
              <p class="text-gray-200">{{ record.fullName }}</p>
            </div>

            <div>
              <label class="block text-gray-400 text-sm">Date of Birth</label>
              <p class="text-gray-200">{{ record.dob }}</p>
            </div>

            <div>
              <label class="block text-gray-400 text-sm">Gender</label>
              <p class="text-gray-200">{{ record.gender }}</p>
            </div>

            <!-- Residence -->
            <div>
              <label class="block text-gray-400 text-sm">Residence</label>
              <div class="flex text-gray-200 items-center gap-2 mt-1">
                <img v-if="resCountry?.flag" :src="resCountry.flag" :alt="`${resCountry.name} flag`" class="w-4 h-4" />
                <span>{{ resCountry?.name || 'Unknown' }}</span>
              </div>
            </div>

            <!-- Nationality -->
            <div>
              <label class="block text-gray-400 text-sm">Nationality</label>
              <div class="flex text-gray-200 items-center gap-2 mt-1">
                <img v-if="natCountry?.flag" :src="natCountry.flag" :alt="`${natCountry.demonym} flag`" class="w-4 h-4" />
                <span>{{ natCountry?.demonym || 'Unknown' }}</span>
              </div>
            </div>

            <div>
              <label class="block text-gray-400 text-sm">Languages</label>
              <div class="mt-1 flex flex-wrap gap-2">
                <img v-for="(flagUrl, index) in languages" :key="index" :src="flagUrl" :alt="`Language flag ${index + 1}`" class="w-6 h-6 rounded-full object-cover" />
              </div>
            </div>

            <div>
              <label class="block text-gray-400 text-sm">Last Login</label>
              <p class="text-gray-200">{{ record.lastLogin }}</p>
            </div>

            <div>
              <label class="block text-gray-400 text-sm">Contact ID</label>
              <p class="text-gray-200">{{ record.uid }}</p>
            </div>

            <div>
              <label class="block text-gray-400 text-sm">Last Location</label>
              <p class="text-gray-200">{{ address }}</p>
            </div>

          </div>
        </div>

        <!-- Right side - Media List and Preview -->
        <div class="flex-1 flex flex-col gap-6">
          <!-- Media List -->
          <div class="bg-gray-700 p-5 rounded-md flex-1 overflow-y-auto">
            <h3 class="text-lg font-medium text-white mb-4">Verification Documents</h3>

            <div v-for="(media, index) in mediaItems" :key="index"
                 @click="selectMedia(media)"
                 :class="['media-item cursor-pointer p-3 rounded-md mb-2 hover:bg-gray-600 transition',
                           selectedMedia?.id === media.id ? 'border-2 border-blue-500' : '']">
              <div class="flex justify-between items-center">
                <span class="text-white font-medium">{{ media.label }}</span>
                <div class="flex space-x-1">
                  <span v-if="media.status === 'Approved'" class="text-green-400">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                  </span>
                  <span v-else-if="media.status === 'Rejected'" class="text-red-400">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                  </span>
                  <span v-else class="text-yellow-400">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end gap-3">
            <button @click="confirmSelected('Approved')"
                    :disabled="selectedMedia === null"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-500">
              Approve Selected
            </button>
            <button @click="confirmSelected('Rejected')"
                    :disabled="selectedMedia === null"
                    class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-500">
              Reject Selected
            </button>

            <!-- New Submit Button -->
            <button @click="submitVerification"
                    :disabled="!allProcessed"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-500">
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Image Viewer Modal -->
  <ImageViewer v-if="showImage" :url="mediaUrl" :show="showImage" @close="showImage = false" />

  <!-- Video Player Modal -->
  <VideoPlayer v-if="showVideo" :url="mediaUrl" :show="showVideo" @close="showVideo = false" />
</template>

<script>
import EventBus from "@/utils/EventBus";
import CacheManager from "@/utils/CacheManager";
import {RequestManager} from "@/utils/RequestManager";
import ImageViewer from "@/components/ui/ImageViewer.vue";
import VideoPlayer from "@/components/ui/VideoPlayer.vue";
import {getAddressFromGPS, getCountryData} from "@/utils/geocoding_utils";

export default {
  name: "VerificationPopup",
  components: {VideoPlayer, ImageViewer},
  emits: ['closeModal', 'verifyRecord'],
  props: {
    cache: {
      type: CacheManager,
      required: true,
    },
    request: {
      type: RequestManager,
      required: true,
    },
    record: {
      type: Object,
      required: true
    },
    testing: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      test: [
        "idback.jpg",
        "idfront.jpg",
        "idhand.jpg",
        "passport.jpg",
        "selfie.mp4"
      ],
      uid: -1,
      isID: false,
      address: "",
      mediaUrl: "",
      mediaType: "",
      languages: [],
      natCountry: {},
      resCountry: {},
      mediaItems: [],
      showImage: false,
      showVideo: false,
      canSubmit: false,
      error: undefined,
      selectedMedia: null,
      rejectionComment: "",
    };
  },
  computed: {
    allProcessed() {
      return this.mediaItems.every(item =>
          item.status === 'Approved' || item.status === 'Rejected'
      );
    },
    statusText() {
      return {
        approved: "Approved",
        rejected: "Rejected",
        pending: "Pending"
      };
    }
  },
  methods: {
    async submitVerification() {
      this.$emit('verifyRecord', this.mediaItems);
    },
    async getVerificationData() {
      let data = undefined;
      if(this.testing) {
        data = this.test;
      } else {
        const loginCache = this.cache.loginCache();
        const uid = this.record.uid;
        const token = loginCache.token;
        const userName = loginCache.data;
        const response = await this.request.getUserVerification(uid, userName, token);
        if(response.msg !== "SUCCESS") {
          throw new Error(response.msg);
        }
        data = response.data;
        console.log("response", response);
      }
      if(!(data && data.length > 0)) {
        return EventBus.emit('completedRecord', {
          type: "ignore",
          record: this.record,
        });
      }
      for (const item of data) {
        const name = this.getName(item);
        if(!name) continue;
        if(name === "ID Hand") {
          this.isID = true;
        }
        const mediaType = String(item).toLowerCase().endsWith('.mp4') ? "video": "image";
        this.mediaItems.push({
          id: item,
          label: name,
          type: mediaType,
          status: "pending",
        });
      }
      await this.loadProfile();
    },
    getName(mediaName) {
      const strName = String(mediaName).toLowerCase();
      if(strName.startsWith('selfie')) return "Selfie";
      if(strName.startsWith('idback')) return "ID Back";
      if(strName.startsWith('idhand')) return "ID Hand";
      if(strName.startsWith('idfront')) return "ID Front";
      if(strName.startsWith('passport')) return "Passport";
    },
    async loadProfile() {
      this.getLangs();
      this.getLocation();
      this.resCountry = getCountryData(this.record.residence);
      this.natCountry = getCountryData(this.record.nationality);
    },
    async getLocation() {
      const coords = this.record.gps;
      this.address = await getAddressFromGPS(coords);
    },
    getLangs() {
      const langs = this.record.languages;
      if(!(langs && langs.length)) return;
      const spl = langs.split(',');
      for (const lang of spl) {
        const info = getCountryData(lang);
        if(info && info.flag) this.languages.push(info.flag);
      }
      console.log(spl);
    },
    selectMedia(media) {
      this.selectedMedia = media;
      this.mediaUrl = "";
      this.mediaType = media.type;
      if (this.mediaType === "image") this.showImage = true;
      else if (this.mediaType === "video") this.showVideo = true;
      console.log("mediaType", this.mediaType);
      this.fetchMediaUrl(media);
    },
    async fetchMediaUrl(media) {
      console.log("media", media);
      try {
        let data;
        if(this.testing) {
          if (this.mediaType === "image") {
            data = {
              "msg": "SUCCESS",
              "data": {
                "url": `https://picsum.photos/800/600?random=${Date.now()}`,
                "expires_in": 600
              }
            }
          }
          else if (this.mediaType === "video") {
            data = {
              "msg": "SUCCESS",
              "data": {
                "url": `https://demo.sirv.com/videos/e-tron_quattro.mp4`,
                "expires_in": 600
              }
            }
          }

        }
        else {
          const loginCache = this.cache.loginCache();
          const uid = this.uid;
          const token = loginCache.token;
          const userName = loginCache.data;
          console.log("request", uid, userName, token, media.id);
          const response = await this.request.getUserMedia(uid, userName, token, media.id);
          console.log("fetchMediaUrl", media, response);
          if(response.msg !== "SUCCESS") {
            throw new Error(response.msg);
          }
          data = response.data;
        }
        if(!data) {
          throw new Error("Request failed!");
        }

        const url = data.url;
        if(!String(url).toLowerCase().startsWith("https")) {
          throw new Error("Document not found!");
        }
        this.mediaUrl = url;
        console.log("mediaUrl", this.mediaUrl)
        this.$forceUpdate();
      } catch (e) {
        console.error(e);
      }
    },
    confirmSelected(status) {
      this.updateMediaStatus(status);
    },
    updateMediaStatus(status, comment = undefined) {
      const index = this.mediaItems.findIndex(item => item.id === this.selectedMedia.id);
      if(index === -1) {
        return alert("Unknown selected media");
      }
      this.mediaItems[index].status = status;
      this.mediaItems[index].comment = comment;
    }
  },
  mounted() {
    this.uid = this.record.uid;
    this.getVerificationData();
  }
};
</script>
